/**
 * Shared sport/team context normalization for chat surfaces.
 *
 * Normalizes selector/team config context into a stable payload shape that can
 * be reused across Ask SLAM AI frontends and backend endpoints.
 */

const BASEBALL_CODES = new Set([
  'BASEBALL',
  'MLB',
  'MILB',
  'CBB',
  'NPB',
  'KBO',
]);

const BASKETBALL_CODES = new Set([
  'BASKETBALL',
  'NBA',
  'WNBA',
  'MBB',
  'WBB',
  'GL',
  'GDL',
  'EURO',
  'EUROBB',
  'ASIABB',
  'AMERBB',
  'WBPRO',
  'MB2',
  'MB3',
  'MBIA',
  'MBJC',
  'WB2',
  'WB3',
  'WBIA',
  'WBJC',
]);

const WOMENS_CODE_PREFIXES = ['WBB', 'WB', 'WNBA', 'WHK', 'WVB', 'WBPRO'];
const MENS_CODE_PREFIXES = ['MBB', 'MB', 'MLB', 'NBA', 'MHK', 'CFB', 'NFL'];

function toUpperToken(value) {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).trim().toUpperCase();
}

export function inferGenderFromLeagueCode(leagueCode) {
  const code = toUpperToken(leagueCode);
  if (!code) {
    return null;
  }

  if (WOMENS_CODE_PREFIXES.some((prefix) => code.startsWith(prefix))) {
    return 'women';
  }
  if (MENS_CODE_PREFIXES.some((prefix) => code.startsWith(prefix))) {
    return 'men';
  }
  return 'coed';
}

export function inferSportCategory(input) {
  const token = toUpperToken(input);
  if (!token) {
    return null;
  }

  if (token === 'BASEBALL' || BASEBALL_CODES.has(token)) {
    return 'baseball';
  }
  if (token === 'BASKETBALL' || BASKETBALL_CODES.has(token)) {
    return 'basketball';
  }

  return null;
}

function normalizeCategory(category, fallbackCode) {
  const direct = inferSportCategory(category);
  if (direct) {
    return direct;
  }
  return inferSportCategory(fallbackCode);
}

export function normalizeSelectedTeamSelection(selection, fallback = {}) {
  const source = selection || {};
  const league = source.league || {};
  const team = source.team || {};

  const leagueCode = league.code || fallback.leagueCode || null;
  const sportCode = source.sportCode || leagueCode || fallback.sportCode || null;
  const sportCategory = normalizeCategory(source.sport, sportCode) || fallback.sportCategory || null;
  const gender = source.gender || inferGenderFromLeagueCode(leagueCode) || fallback.gender || null;

  return {
    ...source,
    sport: sportCategory || source.sport || null,
    sportCategory: sportCategory || null,
    sportCode: sportCode || null,
    league: league,
    leagueCode: leagueCode || null,
    leagueName: league.name || fallback.leagueName || null,
    leagueLevel: league.level || fallback.leagueLevel || null,
    team: team,
    teamId: team.team_id || team.id || team.team_code || fallback.teamId || null,
    teamName: team.team_name || team.full_name || team.name || fallback.teamName || null,
    teamAbbrev: team.team_code || team.abbreviation || team.abbrev || fallback.teamAbbrev || null,
    conference: team.conference || fallback.conference || null,
    gender,
  };
}

export function buildChatSportContext({
  selectedTeam,
  selectedSport,
  sportCode,
  teamConfig,
  sportHint,
} = {}) {
  const normalizedSelection = normalizeSelectedTeamSelection(selectedTeam, {
    sportCategory: inferSportCategory(selectedSport || sportHint || sportCode),
    sportCode: sportCode || null,
    teamName: teamConfig?.name || null,
    teamId: teamConfig?.id || null,
  });

  const resolvedSportCategory = normalizedSelection.sportCategory
    || inferSportCategory(sportHint)
    || inferSportCategory(selectedSport)
    || inferSportCategory(sportCode)
    || inferSportCategory(teamConfig?.sportCode)
    || 'baseball';

  const resolvedSportCode = normalizedSelection.sportCode
    || sportCode
    || teamConfig?.sportCode
    || (resolvedSportCategory === 'baseball' ? 'MLB' : 'MBB');

  return {
    sportCategory: resolvedSportCategory,
    sportCode: resolvedSportCode,
    leagueCode: normalizedSelection.leagueCode || resolvedSportCode,
    leagueName: normalizedSelection.leagueName || null,
    leagueLevel: normalizedSelection.leagueLevel || null,
    gender: normalizedSelection.gender || inferGenderFromLeagueCode(resolvedSportCode),
    teamId: normalizedSelection.teamId || teamConfig?.id || null,
    teamName: normalizedSelection.teamName || teamConfig?.name || null,
    teamAbbrev: normalizedSelection.teamAbbrev || null,
    conference: normalizedSelection.conference || null,
    selectedAt: normalizedSelection.selectedAt || null,
    selectedTeam: normalizedSelection,
    isBaseball: resolvedSportCategory === 'baseball',
    isBasketball: resolvedSportCategory === 'basketball',
  };
}

export function buildChatContextPayload(chatContext = {}, extra = {}) {
  return {
    sport: chatContext.leagueCode || chatContext.sportCode || chatContext.sportCategory || null,
    sportCategory: chatContext.sportCategory || null,
    sportType: chatContext.sportCategory || null,
    sportCode: chatContext.sportCode || null,
    league: chatContext.leagueCode || null,
    leagueCode: chatContext.leagueCode || null,
    leagueName: chatContext.leagueName || null,
    leagueLevel: chatContext.leagueLevel || null,
    gender: chatContext.gender || null,
    team: chatContext.teamName || null,
    teamId: chatContext.teamId || null,
    teamName: chatContext.teamName || null,
    teamAbbrev: chatContext.teamAbbrev || null,
    conference: chatContext.conference || null,
    ...extra,
  };
}

export default {
  buildChatSportContext,
  buildChatContextPayload,
  inferGenderFromLeagueCode,
  inferSportCategory,
  normalizeSelectedTeamSelection,
};
